import { createContext, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import dayjs from "dayjs";

import useAxios from "../../utils/useAxios";
import { resObj } from "../../utils/constants";

const BufferContext = createContext();

export default BufferContext;

export const BufferProvider = ({ children }) => {
  const api = useAxios();
  const navigate = useNavigate();

  const componentIndexRef = useRef(0);

  const getComponentObj = () => ({
    index: componentIndexRef.current++,
    id: '',
    buffer_component_name: '', 
    buffer_component_concentration_mmol: '', 
  });

  const initValues = {
    today: dayjs(Date.now()),
    bufferAcronymArray: [],
    operatorArray: [],
    bufferComponentNameArray: [],
  };
  const [initialValues, setInitialValues] = useState(initValues);

  const bufferObj = {
    uniqueId: '',
    datePrepared: null,
    bufferAcronym: null, 
    targetPH: '', 
    measuredPH: '',
    bufferConcentration: '',
    operatorArray: [],
  };
  const [bufferData, setBufferData] = useState(bufferObj);
  const [bufferComponentArray, setBufferComponentArray] = useState([]);
  const [apiRes, setApiRes] = useState(resObj);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmit, setIsSubmit] = useState(false);
  const [bufferId, setBufferId] = useState('');
  const [deletedIdArray, setDeletedIdArray] = useState([]);
  const [showPointer, setShowPointer] = useState(false);

  useEffect(() => {
    getInitialData();
    getOperators();
    handleComponentAdd();
  }, []);

  const getInitialData = async () => {
    try {
      setApiRes({...apiRes, loading: true});
      const response = await api.get('/api/materials-capture/buffer/initial/data/get');
      
      if (response.status === 200) {                            
        setInitialValues(prev => ({
          ...prev,
          bufferAcronymArray: response.data.buffer_acronym_list,
          bufferComponentNameArray: response.data.buffer_component_name_list,
        }));
        setApiRes({ ...apiRes, loading: false });
      }
    } catch (err){
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Buffer Init Data Get',
        loading: false,
      });
    }
  };

  const getOperators = async () => {
    try {
      setApiRes({...apiRes, loading: true});
      const response = await api.get('/api/materials-capture/operators/get');
      
      if (response.status === 200) {        
        setInitialValues(prev => ({
          ...prev,
          operatorArray: response.data,
        }))
        setApiRes({ ...apiRes, loading: false });
      }
    } catch (err){
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Operators Get',
        loading: false,
      });
    }
  };

  const validate = () => {
    const errors = {};

    if (bufferData?.operatorArray.length === 0) {
      errors['operator'] = 'Operator is required!';
    }
    if (!bufferData?.datePrepared) {
      errors['datePrepared'] = 'Date Prepared is required!';
    }
    if (!bufferData?.bufferAcronym) {
      errors['bufferAcronym'] = 'Buffer Acronym is required!';
    }
    if (!bufferData?.targetPH) {
      errors['targetPH'] = 'Target pH is required!';
    }
    if (!bufferData?.measuredPH) {
      errors['measuredPH'] = 'Measured pH is required!';
    }
    if (!bufferData?.bufferConcentration) {
      errors['bufferConcentration'] = 'Buffer concentration (mmol) is required!';
    }

    bufferComponentArray.forEach((componentItem, index) => {
      const componentIndex = componentItem?.index;

      if (!componentItem.buffer_component_name) {
        errors[`buffer_component_name-${componentIndex}`] = `Buffer component ${index + 1} is required!`;
      }
      if (!componentItem.buffer_component_concentration_mmol) {
        errors[`buffer_component_concentration_mmol-${componentIndex}`] = `Buffer component ${index + 1} concentration is required!`;
      }
    });
    
    return errors;
  };

  const handleSubmit = () => {
    setFormErrors(validate());
    setIsSubmit(true);
  };

  useEffect(() => {
    if (Object.keys(formErrors).length === 0 && isSubmit) {
      submitBuffer();
    }
  }, [formErrors]);

  const submitBuffer = () => {
    const docUrIArr = window.location.href.split('/');
    const uriEnd = docUrIArr[docUrIArr.length - 1];
    if (uriEnd === 'create') {
      createBuffer();
    } else {
      updateBuffer();
    }
  };

  const createBuffer = async () => {
    try {
      setApiRes({...apiRes, loading: true});

      const response = await api.post('/api/materials-capture/buffer/create', {
        operator_list: bufferData?.operatorArray,
        unique_id: bufferData?.uniqueId,
        date_prepared: bufferData?.datePrepared,
        buffer_acronym: bufferData?.bufferAcronym,
        target_ph: bufferData?.targetPH,
        measured_ph: bufferData?.measuredPH,
        buffer_concentration: bufferData?.bufferConcentration,
        buffer_components_list: bufferComponentArray,
      });

      if (response.status === 200) {
        setApiRes({
          ...apiRes,
          showAlert: true,
          successMsg: response.data,
          loading: false,
        });
        navigate('/buffers');
      }
    } catch (err) {
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Buffer create',
        loading: false,
      });
    }
  };

  const updateBuffer = async () => {
    try {
      setApiRes({...apiRes, loading: true});

      const response = await api.post('/api/materials-capture/buffer/edit', {
        buffer_id: bufferId,
        operator_list: bufferData?.operatorArray,
        date_prepared: bufferData?.datePrepared,
        buffer_acronym: bufferData?.bufferAcronym,
        target_ph: bufferData?.targetPH,
        measured_ph: bufferData?.measuredPH,
        buffer_concentration: bufferData?.bufferConcentration,
        buffer_components_list: bufferComponentArray,
        deleted_id_list: deletedIdArray,
      });

      if (response.status === 200) {
        setApiRes({
          ...apiRes,
          showAlert: true,
          successMsg: response.data,
          loading: false,
        });
      }
    } catch (err) {
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Buffer update',
        loading: false,
      });
    }
  };

  const handleComponentAdd = () => {
    setBufferComponentArray(prev => [...prev, getComponentObj()]);
  };

  const handleComponentInputChange = (e, index, key, value) => {    
    let data = [...bufferComponentArray];    
    data = data.map((obj) =>
      obj?.index === index
        ? { ...obj, [key]: value }
        : obj
    );
    setBufferComponentArray(data);
  };

  const handleComponentDelete = (index, id) => {
    if (bufferComponentArray.length > 1) {
      setDeletedIdArray(prev => [...prev, id]);
      setBufferComponentArray(prev => prev.filter(item => item?.index !== index));
    }
  };

  useEffect(() => {
    if (bufferId !== '') getBufferDetails()
  }, [bufferId]);

  useEffect(() => {
    setShowPointer(bufferComponentArray.length !== 1);
  }, [bufferComponentArray]);

  const getBufferDetails = async () => {
    try {
      setApiRes({...apiRes, loading: true});
      const response = await api.get(
        '/api/materials-capture/buffer/details/get', {
          params: {
            buffer_id: bufferId
          }
        });
      
      if (response.status === 200) {
        const bufferData = response.data.buffer_data;        
        setBufferData(prev => ({
          ...prev,
          uniqueId: bufferData.unique_id,
          operatorArray: bufferData.operator_list,
          datePrepared: bufferData.date_prepared,
          bufferAcronym: bufferData.buffer_acronym,
          targetPH: bufferData.buffer_target_ph,
          measuredPH: bufferData.buffer_measured_ph,
          bufferConcentration: bufferData.buffer_concentration_mmol,
        }));
        const bufferComponentData = [];
        for (const item of response.data.buffer_component_concentrations_data) {
          item['index'] = componentIndexRef.current++;
          bufferComponentData.push(item);
        };        
        setBufferComponentArray(bufferComponentData);
        setApiRes({ ...apiRes, loading: false });
      }
    } catch (err){
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Buffer details Get',
        loading: false,
      });
    }
  };

  const handleClone = () => {
    navigate('/buffer/create');
  };

  const contextData = {
    initialValues: initialValues,
    bufferData: bufferData,
    setBufferData: setBufferData,
    formErrors: formErrors,
    handleSubmit: handleSubmit,
    apiRes: apiRes,
    setApiRes: setApiRes,
    handleComponentInputChange: handleComponentInputChange,
    bufferComponentArray: bufferComponentArray,
    handleComponentDelete: handleComponentDelete,
    handleComponentAdd: handleComponentAdd,
    setBufferId: setBufferId,
    showPointer: showPointer,
    handleClone: handleClone,
  };

  return (
    <BufferContext.Provider value={contextData}>
      {children}
    </BufferContext.Provider>
  )
};